import type { z } from 'zod';

import { createRequestTransport } from '../../api/transport';
import { clearAdminSession, getAdminSession } from '../session-store';

export { ApiError } from '../../api/transport';

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Wired once by the admin shell to redirect to /admin/entrar on any 401. */
export function setAdminUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

const request = createRequestTransport({
  getSession: getAdminSession,
  onUnauthorized: () => {
    clearAdminSession();
    unauthorizedHandler?.();
  },
});

export function adminApiGet<TSchema extends z.ZodType>(path: string, schema: TSchema) {
  return request(path, schema, { method: 'GET' });
}

export function adminApiPost<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body?: unknown,
  idempotencyKey?: string,
) {
  return request(path, schema, {
    method: 'POST',
    body: body ?? {},
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/**
 * `idempotencyKey` is optional because not every PATCH needs one: a
 * last-write-wins field patch (the platform Gasto) is idempotent by nature,
 * while a username change rides the replay ledger and passes a key.
 */
export function adminApiPatch<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  idempotencyKey?: string,
) {
  return request(path, schema, {
    method: 'PATCH',
    body,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/** Full-state set of one resource; admin writes always ride the replay ledger. */
export function adminApiPut<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  body: unknown,
  idempotencyKey: string,
) {
  return request(path, schema, {
    method: 'PUT',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** `body` is optional: only a delete that names the version it read (`{ expectedVersion }`) carries one. */
export function adminApiDelete<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  idempotencyKey: string,
  body?: unknown,
) {
  return request(path, schema, {
    method: 'DELETE',
    headers: { 'Idempotency-Key': idempotencyKey },
    ...(body === undefined ? {} : { body }),
  });
}
